import {AceLoggerService} from '@logging/ace-logger.service';
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {FilesResolverResponse} from './files.types';
import {BaseService} from '@shared/services/api/base-service';
import {VirtualFile, VirtualFileFormat} from '@app/graphql-typings/graphql';
import {PagedModel} from '@shared/api/model/paged-model';
import {AceLogger} from '@logging/ace-logger';
import {GRAPHQL_TYPES, HttpUtilityService} from "@shared/services/api/http-utility-service";
import {APIResponse} from "@app/layout/admin/services/api/api.interface";
import * as gql from "gql-query-builder";
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {StringUtils} from '@shared/util/general/string-utils';

export enum Intervals {
  P1D = 'P1D',
  P7D = 'P7D',
  P1M = 'P1M'
}

export class FilesSearchTerm {
  site_id?: number;
  file_id?: number;
  format_id?: number;
  filename_pattern?: string;
  exact_filename_pattern?: string;
}

export class FileFormatsSearchTerm {
  format_id?: number;
  format_ids?: number[];
  file_format?: string;

}

interface FileScopeOptions {
  getFileFormat?: boolean;
}

interface VirtualFileAPI extends Omit<VirtualFile, 'format'> {
  file_format?: string;
}

export const vacerFilesResolver: ResolveFn<FilesResolverResponse> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<FilesResolverResponse> => {
  const createActionName: string = 'create';
  const editFile: boolean = route.data.name !== createActionName;
  const fileId: number = StringUtils.stringToNumber(route.paramMap.get('id'));
  if (editFile && (fileId == null || isNaN(fileId))) {
    throw new Error('A valid file ID must be specified');
  }
  const vacerFilesService: VacerFilesService = inject(VacerFilesService);
  const file$: Observable<VirtualFile> = editFile
    ? vacerFilesService.getFileById(fileId).pipe(map((file: VirtualFile) => {
      if (file) {
        return file;
      } else {
        throw new Error('No files found for file ID ' + fileId);
      }
    }))
    : of(null);

  return forkJoin([file$, vacerFilesService.searchAllFileFormats()])
    .pipe(map(([file, fileFormats]: [VirtualFile, VirtualFileFormat[]]) => {
      return <FilesResolverResponse>{file: file, fileFormats: fileFormats};
    }));
};

@Injectable()
export class VacerFilesService extends BaseService {
  private headers: HttpHeaders = this.getHeaders();
  private readonly FILES_PAGE_LIMIT = 2000;
  private readonly FILE_OPERATION = 'file';
  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(VacerFilesService.name);
  }

  // post a new file
  createFile(file: VirtualFile): Observable<VirtualFile> {
    const fileAPI: VirtualFileAPI = this.coerceVirtualFileToVirtualAPI(file);
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/files', fileAPI, options)
      .pipe(map((response: APIResponse<VirtualFile>) => this.coerceVirtualFileAPIToVirtualFile(this.httpUtilityService.extractResponseData(response, this.FILE_OPERATION))));
  }

  updateFile(file: VirtualFile): Observable<VirtualFile> {
    const fileAPI: VirtualFileAPI = this.coerceVirtualFileToVirtualAPI(file);
    const options = {headers: this.headers};
    return this.http.put(this.getGateway() + '/files/' + fileAPI.file_id, fileAPI, options)
      .pipe(map((response: APIResponse<VirtualFile>) => this.coerceVirtualFileAPIToVirtualFile(this.httpUtilityService.extractResponseData(response, this.FILE_OPERATION))));
  }

  private coerceVirtualFileToVirtualAPI(file: VirtualFile): VirtualFileAPI {
    return {
      file_id: file.file_id,
      format_id: file.format_id,
      filename_pattern: file.filename_pattern,
      collection_interval: file.collection_interval,
      site_ids: file.site_ids,
      description: file.description,
      timezone: file.timezone,
      data_interval: file.data_interval,
      decimal_separator: file.decimal_separator,
      grouping_separator: file.grouping_separator
    };
  }

  private coerceVirtualFileAPIToVirtualFile(file: VirtualFileAPI): VirtualFile {
    return {
      file_id: file.file_id,
      format_id: file.format_id,
      format: {format_id: file.format_id, file_format: file.file_format},
      filename_pattern: file.filename_pattern,
      collection_interval: file.collection_interval,
      site_ids: file.site_ids,
      description:  file.description,
      timezone: file.timezone,
      data_interval: file.data_interval,
      decimal_separator: file.decimal_separator,
      grouping_separator: file.grouping_separator
    };
  }

  // Delete a File
  deleteFile(fileId): Observable<VirtualFile> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + '/files/' + fileId,  options)
      .pipe(map((response: APIResponse<VirtualFile>) => this.httpUtilityService.extractResponseData(response, this.FILE_OPERATION)));
  }

  getFileById(fileId: number): Observable<VirtualFile> {
    return this.searchAllFiles({file_id: fileId}, {getFileFormat: true}).pipe(map((files: VirtualFile[]) => CollectionUtils.only(files)));
  }

  searchAllFiles(searchTerm?: FilesSearchTerm, scopeOptions?: FileScopeOptions): Observable<VirtualFile[]> {
    const filesPageWrapper = (pageInfo) => this.searchFiles(pageInfo, searchTerm, scopeOptions);
    return this.httpUtilityService.pageUntilComplete(filesPageWrapper, {first: 0, rows: this.FILES_PAGE_LIMIT})
      .pipe(map((files: VirtualFile[]) => files.sort(this.sortByFilenamePattern)));
  }


  searchFiles(pageInfo: {first: number, rows: number}, searchTerm: FilesSearchTerm, scopeOptions?: FileScopeOptions):  Observable<PagedModel<VirtualFile[]>> {
    if (searchTerm.exact_filename_pattern != null) {
      searchTerm.filename_pattern = searchTerm.exact_filename_pattern;
      delete searchTerm.exact_filename_pattern;
    } else if (searchTerm.filename_pattern) {
      searchTerm.filename_pattern = '%' + searchTerm.filename_pattern + '%';
    }

    const fileQuery = {
      operation: 'virtual_files',
      fields: [
        'file_id',
        'format_id',
        'filename_pattern',
        'collection_interval',
        'site_ids',
        'description',
        'timezone',
        'data_interval',
        'decimal_separator',
        'grouping_separator',
        ...scopeOptions?.getFileFormat ? [ {format: ['file_format', 'format_id']}] : []
      ],
      variables: {
        site_id: {type: GRAPHQL_TYPES.INTEGER, required: false},
        file_id: {type: GRAPHQL_TYPES.INTEGER, required: false},
        format_id: {type: GRAPHQL_TYPES.INTEGER, required: false},
        filename_pattern: {type: GRAPHQL_TYPES.STRING, required: false},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    };
    const query = gql.query(fileQuery);
    const variables = {...searchTerm, ... {start_index: pageInfo.first, limit: pageInfo.rows}};
    return this.httpUtilityService.filesGraphQL<VirtualFile[]>(query, fileQuery.operation, variables);
  }

  public sortByFilenamePattern = (a: VirtualFile, b: VirtualFile): number => (a?.filename_pattern ?? '').localeCompare(b?.filename_pattern ?? '');

  searchAllFileFormats(searchTerm?: FileFormatsSearchTerm): Observable<VirtualFileFormat[]> {
    const fileFormatPageWrapper = (pageInfo) => this.searchFileFormats(pageInfo, searchTerm);
    return this.httpUtilityService.pageUntilComplete(fileFormatPageWrapper, {first: 0, rows: this.FILES_PAGE_LIMIT});
  }

  private searchFileFormats(pageInfo: {first: number, rows: number}, searchTerm: FileFormatsSearchTerm):  Observable<PagedModel<VirtualFileFormat[]>> {
    const fileFormatQuery = {
      operation: 'virtual_file_formats',
      fields: ['format_id', 'file_format'],
      variables: {
        format_id: {type: GRAPHQL_TYPES.INTEGER, required: false},
        format_ids: {type: GRAPHQL_TYPES.INTEGER, list: true, required: false},
        file_format: {type: GRAPHQL_TYPES.STRING, required: false},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    };
    const query = gql.query(fileFormatQuery);
    const variables = {...searchTerm, ... {start_index: pageInfo.first, limit: pageInfo.rows}};
    return this.httpUtilityService.filesGraphQL<VirtualFileFormat[]>(query, fileFormatQuery.operation, variables);
  }

  collectionISO8601IntervalToDisplayFormat(interval: string): string {
    if (!interval) {
      return 'Ad-hoc';
    }
    switch (interval) {
      case Intervals.P1D:
        return 'Daily';
      case Intervals.P7D:
        return 'Weekly';
      case Intervals.P1M:
        return 'Monthly';
      default:
        throw new Error('Unexpected interval "' + interval + '"');
    }
  }

}
